import { getCompanies, getUserTrendStats, queryKeys } from "@dutyon/apis";
import {
  ActionIcon,
  Badge,
  Card,
  Grid,
  Group,
  Loader,
  LoadingOverlay,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  TitleContainer,
  Tooltip,
} from "@dutyon/ui-kit";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  Activity,
  Building2,
  Calendar,
  ClipboardList,
  Clock,
  HeartHandshake,
  MessageSquare,
  RefreshCw,
  TrendingUp,
  UserCheck,
  UserPlus,
  Users,
  UserX,
} from "lucide-react";
import { useMemo, useState } from "react";

export function UserTrendStatisticsPage() {
  const [periodKey, setPeriodKey] = useState<string>("30d");
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);

  // 병원 목록 조회 (셀렉트 옵션용)
  const { data: companiesData } = useQuery({
    queryKey: queryKeys.companies.all,
    queryFn: () => getCompanies(),
  });

  const companyOptions = useMemo(() => {
    const defaultOption = [{ value: "all", label: "전체 병원" }];
    if (!companiesData) return defaultOption;
    return [
      ...defaultOption,
      ...companiesData.map((comp) => ({
        value: comp.id,
        label: `${comp.name} (${comp.code})`,
      })),
    ];
  }, [companiesData]);

  const queryParams = useMemo(() => {
    const today = dayjs().format("YYYY-MM-DD");
    let startDate = dayjs().subtract(29, "day").format("YYYY-MM-DD");

    switch (periodKey) {
      case "7d":
        startDate = dayjs().subtract(6, "day").format("YYYY-MM-DD");
        break;
      case "month":
        startDate = dayjs().startOf("month").format("YYYY-MM-DD");
        break;
      case "90d":
        startDate = dayjs().subtract(89, "day").format("YYYY-MM-DD");
        break;
      case "1y":
        startDate = dayjs().subtract(364, "day").format("YYYY-MM-DD");
        break;
      default:
        startDate = dayjs().subtract(29, "day").format("YYYY-MM-DD");
        break;
    }

    return {
      startDate,
      endDate: today,
      companyId: selectedCompanyId && selectedCompanyId !== "all" ? selectedCompanyId : undefined,
    };
  }, [periodKey, selectedCompanyId]);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: queryKeys.statistics.userTrends(queryParams),
    queryFn: () => getUserTrendStats(queryParams),
  });

  const maxDailyNewUsers = useMemo(() => {
    if (!data?.timeline?.length) return 1;
    return Math.max(...data.timeline.map((item) => item.newUsers), 1);
  }, [data?.timeline]);

  const maxDailyActivities = useMemo(() => {
    if (!data?.timeline?.length) return 1;
    return Math.max(...data.timeline.map((item) => item.totalActivities), 1);
  }, [data?.timeline]);

  return (
    <TitleContainer
      size="xl"
      title={
        <Group justify="space-between" align="center" wrap="wrap" style={{ width: "100%" }}>
          <Group gap="sm">
            <ThemeIcon size="lg" radius="md" variant="light" color="teal">
              <Users size={22} />
            </ThemeIcon>
            <Stack gap={2}>
              <Title order={3}>사용자 성장 및 활동 통계</Title>
              <Text size="xs" c="dimmed">
                사용자 유입 추이와 출퇴근 체크, 원티드 신청, 근무 요청 등 주요 행동을 분석합니다.
              </Text>
            </Stack>
          </Group>
          <Group gap="xs">
            <Select
              placeholder="병원 선택"
              data={companyOptions}
              value={selectedCompanyId || "all"}
              onChange={(val) => setSelectedCompanyId(val === "all" ? null : val)}
              size="xs"
              style={{ width: 180 }}
              allowDeselect={false}
              leftSection={<Building2 size={13} />}
            />
            <SegmentedControl
              value={periodKey}
              onChange={setPeriodKey}
              size="xs"
              radius="md"
              data={[
                { label: "최근 7일", value: "7d" },
                { label: "최근 30일", value: "30d" },
                { label: "이번 달", value: "month" },
                { label: "최근 90일", value: "90d" },
                { label: "1년", value: "1y" },
              ]}
            />
            <Tooltip label="새로고침">
              <ActionIcon
                variant="default"
                size="input-xs"
                radius="md"
                onClick={() => refetch()}
                loading={isFetching}
              >
                <RefreshCw size={14} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      }
    >
      <Stack gap="lg" style={{ position: "relative" }}>
        <LoadingOverlay visible={isLoading || isFetching} overlayProps={{ blur: 1 }} />

        {/* 요약 KPI 카드 4종 */}
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder radius="md" p="lg">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Text size="xs" c="dimmed" fw={700}>
                    누적 전체 사용자
                  </Text>
                  <Title order={2} c="blue">
                    {(data?.summary.totalUsers ?? 0).toLocaleString()}
                    <Text span size="sm" c="dimmed" fw={500} ml={4}>
                      명
                    </Text>
                  </Title>
                </Stack>
                <ThemeIcon size={44} radius="md" color="blue" variant="light">
                  <Users size={22} />
                </ThemeIcon>
              </Group>
              <Group justify="space-between" mt="xs" align="center">
                <Text size="xs" c="dimmed">
                  탈퇴 계정
                </Text>
                <Badge size="xs" variant="subtle" color="gray" leftSection={<UserX size={10} />}>
                  {data?.summary.withdrawnUsersCount ?? 0}명
                </Badge>
              </Group>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder radius="md" p="lg">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Text size="xs" c="dimmed" fw={700}>
                    기간 내 신규 가입
                  </Text>
                  <Title order={2} c="teal">
                    {(data?.summary.newUsersInPeriod ?? 0).toLocaleString()}
                    <Text span size="sm" c="dimmed" fw={500} ml={4}>
                      명
                    </Text>
                  </Title>
                </Stack>
                <ThemeIcon size={44} radius="md" color="teal" variant="light">
                  <UserPlus size={22} />
                </ThemeIcon>
              </Group>
              <Text size="xs" c="dimmed" mt="xs">
                선택한 조회 기간 동안 가입한 회원
              </Text>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder radius="md" p="lg">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Text size="xs" c="dimmed" fw={700}>
                    병원 소속 멤버
                  </Text>
                  <Title order={2} c="indigo">
                    {(data?.summary.activeNursesCount ?? 0).toLocaleString()}
                    <Text span size="sm" c="dimmed" fw={500} ml={4}>
                      명
                    </Text>
                  </Title>
                </Stack>
                <ThemeIcon size={44} radius="md" color="indigo" variant="light">
                  <UserCheck size={22} />
                </ThemeIcon>
              </Group>
              <Text size="xs" c="dimmed" mt="xs">
                병원/부서에 배치된 활성 멤버
              </Text>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder radius="md" p="lg">
              <Group justify="space-between" align="flex-start">
                <Stack gap={4}>
                  <Text size="xs" c="dimmed" fw={700}>
                    기간 내 총 활동량
                  </Text>
                  <Title order={2} c="violet">
                    {(data?.summary.totalActivitiesInPeriod ?? 0).toLocaleString()}
                    <Text span size="sm" c="dimmed" fw={500} ml={4}>
                      건
                    </Text>
                  </Title>
                </Stack>
                <ThemeIcon size={44} radius="md" color="violet" variant="light">
                  <Activity size={22} />
                </ThemeIcon>
              </Group>
              <Text size="xs" c="dimmed" mt="xs">
                출퇴근 + 원티드 + 근무 요청 등 전체 액션
              </Text>
            </Card>
          </Grid.Col>
        </Grid>

        {/* 핵심 행동별 요약 카드 4종 */}
        <Grid gutter="sm">
          <Grid.Col span={{ base: 6, md: 3 }}>
            <Paper p="sm" withBorder radius="md" bg="blue.0">
              <Group justify="space-between">
                <Group gap="xs">
                  <ThemeIcon size="sm" color="blue" variant="filled" radius="xl">
                    <Clock size={12} />
                  </ThemeIcon>
                  <Text size="xs" fw={700}>
                    출퇴근 기록
                  </Text>
                </Group>
                <Text size="sm" fw={800} c="blue">
                  {(data?.activitySummary.attendanceCount ?? 0).toLocaleString()}건
                </Text>
              </Group>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 6, md: 3 }}>
            <Paper p="sm" withBorder radius="md" bg="pink.0">
              <Group justify="space-between">
                <Group gap="xs">
                  <ThemeIcon size="sm" color="pink" variant="filled" radius="xl">
                    <HeartHandshake size={12} />
                  </ThemeIcon>
                  <Text size="xs" fw={700}>
                    원티드 신청
                  </Text>
                </Group>
                <Text size="sm" fw={800} c="pink">
                  {(data?.activitySummary.wantedCount ?? 0).toLocaleString()}건
                </Text>
              </Group>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 6, md: 3 }}>
            <Paper p="sm" withBorder radius="md" bg="orange.0">
              <Group justify="space-between">
                <Group gap="xs">
                  <ThemeIcon size="sm" color="orange" variant="filled" radius="xl">
                    <ClipboardList size={12} />
                  </ThemeIcon>
                  <Text size="xs" fw={700}>
                    근무표 요청
                  </Text>
                </Group>
                <Text size="sm" fw={800} c="orange">
                  {(data?.activitySummary.dutyRequestCount ?? 0).toLocaleString()}건
                </Text>
              </Group>
            </Paper>
          </Grid.Col>

          <Grid.Col span={{ base: 6, md: 3 }}>
            <Paper p="sm" withBorder radius="md" bg="gray.0">
              <Group justify="space-between">
                <Group gap="xs">
                  <ThemeIcon size="sm" color="gray" variant="filled" radius="xl">
                    <MessageSquare size={12} />
                  </ThemeIcon>
                  <Text size="xs" fw={700}>
                    고객 문의
                  </Text>
                </Group>
                <Text size="sm" fw={800} c="gray.8">
                  {(data?.activitySummary.inquiryCount ?? 0).toLocaleString()}건
                </Text>
              </Group>
            </Paper>
          </Grid.Col>
        </Grid>

        {/* 1) 신규 가입 추이 차트 */}
        <Card withBorder radius="md" p="lg">
          <Group justify="space-between" mb="md">
            <Group gap="xs">
              <TrendingUp size={18} />
              <Title order={4}>일자별 신규 회원 가입 추이</Title>
            </Group>
            <Text size="xs" c="dimmed">
              {queryParams.startDate} ~ {queryParams.endDate}
            </Text>
          </Group>

          {data?.timeline && data.timeline.length > 0 ? (
            <Stack gap="xs" mt="md">
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 6,
                  height: 160,
                  paddingBottom: 24,
                  overflowX: "auto",
                  borderBottom: "1px solid var(--mantine-color-gray-3)",
                }}
              >
                {data.timeline.map((day) => {
                  const heightPercent =
                    day.newUsers > 0
                      ? Math.max(Math.round((day.newUsers / maxDailyNewUsers) * 100), 10)
                      : 2;

                  return (
                    <Tooltip
                      key={day.date}
                      label={
                        <Stack gap={2} p={2}>
                          <Text fw={700} size="xs">
                            {day.date}
                          </Text>
                          <Text size="xs">신규 가입: {day.newUsers}명</Text>
                          <Text size="xs" c="dimmed">
                            누적 가입자: {day.cumulativeUsers}명
                          </Text>
                        </Stack>
                      }
                      withArrow
                    >
                      <div
                        style={{
                          flex: 1,
                          minWidth: 16,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          height: "100%",
                          justifyContent: "flex-end",
                          cursor: "pointer",
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            maxWidth: 24,
                            height: `${heightPercent}%`,
                            backgroundColor:
                              day.newUsers > 0
                                ? "var(--mantine-color-teal-6)"
                                : "var(--mantine-color-gray-2)",
                            borderRadius: "4px 4px 0 0",
                            transition: "height 0.3s ease",
                          }}
                        />
                        <Text
                          size="9px"
                          c="dimmed"
                          style={{
                            marginTop: 4,
                            whiteSpace: "nowrap",
                            transform: data.timeline.length > 15 ? "rotate(-45deg)" : "none",
                            transformOrigin: "center top",
                          }}
                        >
                          {day.date.slice(5)}
                        </Text>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            </Stack>
          ) : (
            <Paper p="xl" withBorder radius="md" style={{ textAlign: "center" }}>
              <Text c="dimmed" size="sm">
                해당 기간 동안의 신규 가입 내역이 없습니다.
              </Text>
            </Paper>
          )}
        </Card>

        {/* 2) 일자별 활동량 복합 차트 */}
        <Card withBorder radius="md" p="lg">
          <Group justify="space-between" mb="md">
            <Group gap="xs">
              <Activity size={18} />
              <Title order={4}>일자별 핵심 행동(활동) 추이</Title>
            </Group>
            <Group gap="md">
              <Group gap={6}>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    backgroundColor: "var(--mantine-color-blue-6)",
                    borderRadius: 2,
                  }}
                />
                <Text size="xs" c="dimmed">
                  출퇴근 체크
                </Text>
              </Group>
              <Group gap={6}>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    backgroundColor: "var(--mantine-color-pink-6)",
                    borderRadius: 2,
                  }}
                />
                <Text size="xs" c="dimmed">
                  원티드 신청
                </Text>
              </Group>
              <Group gap={6}>
                <div
                  style={{
                    width: 10,
                    height: 10,
                    backgroundColor: "var(--mantine-color-orange-6)",
                    borderRadius: 2,
                  }}
                />
                <Text size="xs" c="dimmed">
                  근무표 요청
                </Text>
              </Group>
            </Group>
          </Group>

          {data?.timeline && data.timeline.length > 0 ? (
            <Stack gap="xs" mt="md">
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 6,
                  height: 180,
                  paddingBottom: 24,
                  overflowX: "auto",
                  borderBottom: "1px solid var(--mantine-color-gray-3)",
                }}
              >
                {data.timeline.map((day) => {
                  const totalHeight =
                    day.totalActivities > 0
                      ? Math.max(Math.round((day.totalActivities / maxDailyActivities) * 100), 8)
                      : 2;

                  return (
                    <Tooltip
                      key={day.date}
                      label={
                        <Stack gap={2} p={2}>
                          <Text fw={700} size="xs">
                            {day.date}
                          </Text>
                          <Text size="xs">총 활동: {day.totalActivities}건</Text>
                          <Text size="xs" c="blue.4">
                            출퇴근: {day.attendanceCount}건
                          </Text>
                          <Text size="xs" c="pink.4">
                            원티드: {day.wantedCount}건
                          </Text>
                          <Text size="xs" c="orange.4">
                            근무요청: {day.dutyRequestCount}건
                          </Text>
                        </Stack>
                      }
                      withArrow
                    >
                      <div
                        style={{
                          flex: 1,
                          minWidth: 16,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          height: "100%",
                          justifyContent: "flex-end",
                          cursor: "pointer",
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            maxWidth: 24,
                            height: `${totalHeight}%`,
                            backgroundColor:
                              day.totalActivities > 0
                                ? "var(--mantine-color-violet-5)"
                                : "var(--mantine-color-gray-2)",
                            borderRadius: "4px 4px 0 0",
                            transition: "height 0.3s ease",
                          }}
                        />
                        <Text
                          size="9px"
                          c="dimmed"
                          style={{
                            marginTop: 4,
                            whiteSpace: "nowrap",
                            transform: data.timeline.length > 15 ? "rotate(-45deg)" : "none",
                            transformOrigin: "center top",
                          }}
                        >
                          {day.date.slice(5)}
                        </Text>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            </Stack>
          ) : (
            <Paper p="xl" withBorder radius="md" style={{ textAlign: "center" }}>
              <Text c="dimmed" size="sm">
                해당 기간 동안의 활동 기록이 없습니다.
              </Text>
            </Paper>
          )}
        </Card>

        {/* 병원별 사용자 및 활동 현황 테이블 */}
        <Card withBorder radius="md" p="lg">
          <Group justify="space-between" mb="md">
            <Group gap="xs">
              <Building2 size={18} />
              <Title order={4}>병원별 사용자 수 및 활동 현황</Title>
            </Group>
            <Badge variant="light" color="teal">
              총 {data?.companies.length ?? 0}개 병원
            </Badge>
          </Group>

          {isLoading ? (
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 60 }}>순위</Table.Th>
                  <Table.Th>병원명 (코드)</Table.Th>
                  <Table.Th style={{ width: 120 }}>소속 멤버 수</Table.Th>
                  <Table.Th style={{ width: 120 }}>출퇴근 기록</Table.Th>
                  <Table.Th style={{ width: 120 }}>원티드 신청</Table.Th>
                  <Table.Th style={{ width: 120 }}>근무표 요청</Table.Th>
                  <Table.Th style={{ width: 130 }}>총 활동량</Table.Th>
                  <Table.Th style={{ width: 130 }}>최근 활동일</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td colSpan={8} align="center" py="xl">
                    <Stack align="center" gap="xs">
                      <Loader size="sm" />
                      <Text size="sm" c="dimmed">
                        병원별 사용자 및 활동 통계를 불러오는 중입니다...
                      </Text>
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          ) : data?.companies && data.companies.length > 0 ? (
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 60 }}>순위</Table.Th>
                  <Table.Th>병원명 (코드)</Table.Th>
                  <Table.Th style={{ width: 120 }}>소속 멤버 수</Table.Th>
                  <Table.Th style={{ width: 120 }}>출퇴근 기록</Table.Th>
                  <Table.Th style={{ width: 120 }}>원티드 신청</Table.Th>
                  <Table.Th style={{ width: 120 }}>근무표 요청</Table.Th>
                  <Table.Th style={{ width: 130 }}>총 활동량</Table.Th>
                  <Table.Th style={{ width: 130 }}>최근 활동일</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.companies.map((company, index) => {
                  return (
                    <Table.Tr
                      key={company.companyId}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        setSelectedCompanyId(
                          selectedCompanyId === company.companyId ? null : company.companyId,
                        )
                      }
                    >
                      <Table.Td>
                        <Badge
                          size="sm"
                          variant={index < 3 ? "filled" : "light"}
                          color={
                            index === 0
                              ? "yellow"
                              : index === 1
                                ? "gray"
                                : index === 2
                                  ? "orange"
                                  : "gray"
                          }
                        >
                          {index + 1}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <Text fw={600} size="sm">
                            {company.companyName}
                          </Text>
                          <Badge size="xs" variant="outline" color="gray">
                            {company.companyCode}
                          </Badge>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={600} c="indigo">
                          {company.memberCount.toLocaleString()}명
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="blue">
                          {company.attendanceCount.toLocaleString()}건
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="pink">
                          {company.wantedCount.toLocaleString()}건
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="orange">
                          {company.dutyRequestCount.toLocaleString()}건
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light" color="violet" size="md">
                          {company.totalActivities.toLocaleString()}건
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          <Calendar size={13} color="gray" />
                          <Text size="xs" c="dimmed">
                            {company.lastActiveAt || "-"}
                          </Text>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          ) : (
            <Paper p="xl" withBorder radius="md" style={{ textAlign: "center" }}>
              <Text c="dimmed" size="sm">
                조회된 병원별 활동 데이터가 없습니다.
              </Text>
            </Paper>
          )}
        </Card>
      </Stack>
    </TitleContainer>
  );
}
